import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { trackPageView, getSessionId, getUserId } from "../utils/pageTracking";
import DropOffLocationCard from "../customized/DropOffLocationCard";
import getCheckOut from "../hooks/getCheckOut";

const DropOffLocations: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showEmailInput, setShowEmailInput] = useState(false);

    useEffect(() => {
        // Track page view
        trackPageView('DropOffLocations', getSessionId(), getUserId());
        
        // Scroll to top when page loads
        window.scrollTo(0, 0);
    }, []);

    const handleViewAllServices = async () => {
        //navigate to sign up page
        setIsLoading(true);
        window.location.href = "/#signUp";
    };

    return (
        <div className="min-h-screen bg-[#fafaf3] pt-16">
            {/* Hero Section */}
            <section className="w-full bg-[#c3d3ab] text-white py-12">
                <div className="container mx-auto px-6 text-center">
                    <h1 className="font-mono text-orange-600 text-4xl md:text-5xl lg:text-6xl mb-6">
                        Drop Off Locations
                    </h1>
                    <p className="text-xl md:text-2xl max-w-3xl mx-auto text-black">
                        Convenient locations for your composting needs. Choose the location that works best for you!
                    </p>
                </div>
            </section>

            {/* Locations Grid */}
            <section className="container mx-auto px-6 py-12">
                <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">

                    {/* RIZE Location */}
                    <DropOffLocationCard
                        title="RIZE"
                        mapUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6718.480043314829!2d-68.7830268!3d44.7820493!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cae4b7989cb1079%3A0xa119a4f41238371a!2s28%20Olive%20St%2C%20Bangor%2C%20ME%2004401!5e1!3m2!1sen!2sus!4v1752431187096!5m2!1sen!2sus"
                        website="https://www.rizemaine.com/"
                        address="28 Olive Street, Bangor, ME 04401"
                        locationNote="Box is located in the rear parking lot"
                        hours="24/7 Access"
                        linkText="Sign Up for RIZE Location"
                        linkHref="/dropOff"
                    />

                    {/* Northwood Portable Buildings Location */}
                    <DropOffLocationCard
                        title="NORTHWOODS"
                        mapUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2829.5863093291086!2d-68.75128742315567!3d44.8299918710707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4ca561438fdbb177%3A0x696105ea6a836525!2sNorthWoods%20Portable%20and%20Steel%20Buildings!5e0!3m2!1sen!2sus!4v1752354310962!5m2!1sen!2sus"
                        website="https://northwoodsportablebuildings.com/"
                        address="663 Stillwater Ave, Bangor, ME 04401"
                        locationNote="Bangor Mall parking lot, near Furniture Mattresses and More, in the Northwoods Portable Buildings lot."
                        hours="24/7 Access"
                        linkText="Sign Up for NORTHWOODS Location"
                        linkHref="/dropOff"
                    />
                </div>
            </section>

            {/* Information Section */}
            <section className="bg-[#c3d3ab] py-12">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold text-[#b05e2c] mb-6">
                        Drop Off Service Information
                    </h2>
                    <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
                        <div className="bg-white/90 rounded-lg p-6">
                            <div className="text-4xl mb-3">🪣</div>
                            <h3 className="text-xl font-semibold text-[#b05e2c] mb-2">Clean Bucket Exchange</h3>
                            <p className="text-gray-700">
                                Bring your full bucket and exchange it for a clean one every time you visit.
                            </p>
                        </div>
                        <div className="bg-white/90 rounded-lg p-6">
                            <div className="text-4xl mb-3">💰</div>
                            <h3 className="text-xl font-semibold text-[#b05e2c] mb-2">$15/Month</h3>
                            <p className="text-gray-700">
                                Affordable monthly subscription for unlimited drop-offs at your chosen location.
                            </p>
                        </div>
                        <div className="bg-white/90 rounded-lg p-6">
                            <div className="text-4xl mb-3">🌱</div>
                            <h3 className="text-xl font-semibold text-[#b05e2c] mb-2">24/7 Access</h3>
                            <p className="text-gray-700">
                                Drop off your compostables whenever it's convenient for you.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="container mx-auto px-6 py-12 text-center">
                <h2 className="text-3xl font-bold text-[#b05e2c] mb-6">
                    Ready to Start Composting?
                </h2>
                <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
                    Choose your preferred drop-off location and sign up today to start making a positive impact on the environment.
                </p>
                
                {showEmailInput && (
                    <div className="max-w-md mx-auto mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address *
                        </label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,3}$"
                            className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="your@email.com"
                        />
                    </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                        onClick={handleViewAllServices}
                        disabled={isLoading}
                        className="bg-white border-2 border-[#b05e2c] text-[#b05e2c] px-8 py-4 rounded-lg hover:bg-[#b05e2c] hover:text-white transition-colors font-semibold text-lg disabled:opacity-50"
                    >
                        {isLoading ? "Processing..." : showEmailInput ? "Sign Up for Drop Off - $15/month" : "View All Services"}
                    </button>
                </div>
            </section>
        </div>
    );
};

export default DropOffLocations;
